import { useContext, useCallback, useState, useEffect } from "react";
import * as Sentry from "@sentry/react";
import { observer } from "mobx-react";
import { UI } from "@wwimmo/ui";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import { MessageType } from "src/components/notifications/Notifier";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { ZipAutocompleteInput } from "./autocompleteInputs/ZipAutocompleteInput";
import { CityAutocompleteInput } from "./autocompleteInputs/CityAutocompleteInput";
import { StreetAutocompleteInput } from "./autocompleteInputs/StreetAutocompleteInput";
import { StreetNumberAutocompleteInput } from "./autocompleteInputs/StreetNumberAutocompleteInput";
import { isEmpty } from "lodash";
import { useSaveFormByShortcut } from "src/hooks/form/useSaveFormByShortcut";
import { useSaveUnsavedChangesInForm } from "src/hooks/save-unsaved-changes-in-form/useSaveUnsavedChangesInForm";
import { FORM_STATUS } from "src/utils/Form";
import { getDateWithLongMonth } from "src/utils/Date";
import { ASSEMBLY_STATE, AssemblyDetailEditFormFields } from "src/stores/assemblies/AssemblyStore";
import { AssemblyRealestateList } from "src/screens/assemblies/assembly/details/realestatelist/AssemblyRealestateList";
import { generatePath, useHistory, useParams } from "react-router";
import { Route, selectRoute } from "src/config/routes";
import { InlineCommunicationTimelineEntry } from "src/screens/assemblies/assembly/communication/InlineCommunicationTimelineEntry";
import { COMMUNICATION_TIMELINE_ENTRY } from "src/stores/CommunicationStore";
import { AssemblyUnlockModal } from "src/screens/assemblies/assembly/agenda/modal/AssemblyUnlockModal";
import "./AssemblyDetailEditForm.css";

interface Params {
    assemblyid: string;
}

export enum ERROR_TYPE {
    NONE = 0,
    ZIP = 1,
    CITY = 2,
    STREET = 3,
    STREETNUMBER = 4,
    FLOOR = 5
}

export enum SEARCH_STATE {
    NONE = 0,
    ZIP = 1,
    CITY = 2,
    STREET = 3,
    STREETNUMBER = 4
}

export enum LIST_TYPE {
    ZIP = "zip",
    CITY = "city",
    STREET = "street",
    STREETNUMBER = "streetnumber"
}

const AssemblyDetailEditFormBase = () => {
    const { assemblyid } = useParams<Params>();
    const { t } = useTranslation();
    const { uiStore, assemblyStore, authStore, agendaitemStore } = useContext(RootStoreContext);
    const history = useHistory();
    const isReadOnly = assemblyStore.isAssemblyReadOnly;

    const onClickUnlockAssembly = useCallback(() => {
        agendaitemStore.setIsModalUnlockDisplayed(true);
    }, [agendaitemStore]);

    assemblyStore.setAssembliesEditFormStatus(FORM_STATUS.NONE);

    const realestate_schema = yup.object().shape({
        date: yup.string().required(t("labels.required_field").toString()),
        state: yup.string().required(t("labels.required_field").toString()),
        zip: yup.string(),
        city: yup.string(),
        street: yup.string(),
        housenumber: yup.string(),
        title: yup.string().required(t("labels.required_field").toString()),
        locationdetail: yup.string(),
        motiondeadline: yup.string()
    });

    const {
        register,
        handleSubmit,
        setValue,
        watch,
        reset,
        getValues,
        formState: { errors, dirtyFields, isDirty }
    } = useForm<AssemblyDetailEditFormFields>({
        mode: "onBlur",
        resolver: yupResolver(realestate_schema),
        defaultValues: {
            date: assemblyStore.currentAssembly?.date || "",
            state: assemblyStore.currentAssembly?.state || ASSEMBLY_STATE.PREPARING,
            zip: assemblyStore.currentAssembly?.zip || "",
            city: assemblyStore.currentAssembly?.city || "",
            street: assemblyStore.currentAssembly?.street || "",
            housenumber: assemblyStore.currentAssembly?.housenumber || "",
            title: assemblyStore.currentAssembly?.title || t("screens.realestate.assembly.defaultTitle").toString(),
            locationdetail: assemblyStore.currentAssembly?.locationdetail || "",
            motiondeadline: assemblyStore.currentAssembly?.motiondeadline || null
        }
    });

    assemblyStore.setAssembliesEditFormHasDirtyFields(!isEmpty(dirtyFields));

    const saveForm = async () => {
        await handleSubmit(handleFormSubmit)();
        if (assemblyStore.isNewAssembly) {
            assemblyStore.setIsNewAssembly(false);
        }
    };

    const generatePathParams = { assemblyid: assemblyid };
    useSaveFormByShortcut({ saveFormFunction: saveForm, isFormDirty: isDirty });
    const { saveFormModal, closeSaveFormModalAndGoBack } = useSaveUnsavedChangesInForm({
        saveFormFunction: saveForm,
        currentFormStatus: assemblyStore.assembliesEditFormStatus,
        exceptionPaths: [`/manager${generatePath(Route.assemblyDetails, generatePathParams)}`],
        isFormDirty:
            (isDirty || assemblyStore.agendaItemsHaveChanged) && !assemblyStore.isAddAssemblyRealestateModalDisplayed
    });

    const watchZip = watch("zip");
    const watchCity = watch("city");
    const watchStreet = watch("street");
    const watchHousenumber = watch("housenumber");
    const watchTitle = watch("title");
    const watchDate = watch("date");

    const setFocus = useCallback((inputElementId: string) => {
        const inputElement: HTMLInputElement | null = document?.getElementById(inputElementId) as HTMLInputElement;

        if (inputElement) {
            const tempEl = document.createElement("input");
            tempEl.style.position = "absolute";
            tempEl.style.top = inputElement.offsetTop + 7 + "px";
            tempEl.style.left = inputElement.offsetLeft + "px";
            tempEl.style.height = "0";
            tempEl.style.opacity = "0";

            document.body.appendChild(tempEl);
            tempEl.focus();

            setTimeout(function () {
                inputElement.focus();
                inputElement.click();

                document.body.removeChild(tempEl);
            }, 100);
        }
    }, []);

    useEffect(() => {
        setFocus("title");
    }, [setFocus]);

    const [zipItems, setZipItems] = useState<string[]>([]);
    const [nameItems, setNameItems] = useState<string[]>([]);
    const [streetItems, setStreetItems] = useState<{ id: string; street: string }[]>([]);
    const [streetNumberItems, setStreetNumberItems] = useState<string[]>([]);
    const [numbersOfSelectedStreet, setNumbersOfSelectedStreet] = useState<string[]>([]);

    const [zipWasSelected, setZipWasSelected] = useState<boolean>(assemblyStore.currentAssembly?.zip ? true : false);
    const [cityWasSelected, setCityWasSelected] = useState<boolean>(assemblyStore.currentAssembly?.city ? true : false);
    const [streetWasSelected, setStreetWasSelected] = useState<boolean>(
        assemblyStore.currentAssembly?.street ? true : false
    );
    const [streetNumberWasSelected, setStreetNumberWasSelected] = useState<boolean>(
        assemblyStore.currentAssembly?.housenumber ? true : false
    );

    const [currentCityName, setCurrentCityName] = useState<string>("");
    const [currentZip, setCurrentZip] = useState<string>("");
    const [currentStreet, setCurrentStreet] = useState<string>("");
    const [currentStreetNumber, setCurrentStreetNumber] = useState<string>("");

    const [searchType, setSearchType] = useState<SEARCH_STATE>(SEARCH_STATE.NONE);
    const [error, setError] = useState<ERROR_TYPE>(ERROR_TYPE.NONE);

    const printStatusMessage = useCallback(
        (message: string, msgType: MessageType) => {
            uiStore.enqueueSnackbar({
                content: message,
                options: {
                    type: msgType,
                    autoClose: msgType === MessageType.ERROR ? false : 3000
                }
            });
        },
        [uiStore]
    );

    const handleFormSubmit = useCallback(
        async (formData: AssemblyDetailEditFormFields) => {
            assemblyStore.setAssembliesEditFormStatus(FORM_STATUS.SAVING_FORM);

            if (!formData.motiondeadline) {
                formData = { ...formData, motiondeadline: null };
            }

            try {
                if (isDirty || assemblyStore.currentAssemblyRealestatesHaveChanged) {
                    if (assemblyStore.currentAssemblyRealestates.length === 0) {
                        uiStore.printStatusMessage(
                            t("screens.realestate.assembly.no_realestate_warning"),
                            MessageType.WARNING
                        );
                        return;
                    }
                    await assemblyStore.saveCurrentAssembly(formData, assemblyid);
                    await assemblyStore.loadCurrentAssembly(assemblyid, true);

                    if (assemblyStore.isNewAssembly) {
                        uiStore.setHasNewAssembliesData(true);
                        assemblyStore.setIsNewAssembly(false);
                    }

                    reset(
                        {
                            ...formData,
                            id: assemblyid
                        },
                        {
                            keepDirty: false,
                            keepDefaultValues: false,
                            keepValues: true
                        }
                    );
                }

                assemblyStore.setShowRefetchAssemblyData(true);
                printStatusMessage(t("screens.realestate.assembly.edit.save_success"), MessageType.INFO);
            } catch (error: any) {
                printStatusMessage(
                    t("screens.realestate.assembly.edit.save_error", {
                        errorMessage: error.message
                    }),
                    MessageType.ERROR
                );
            } finally {
                assemblyStore.setAssembliesEditFormStatus(FORM_STATUS.NONE);
                closeSaveFormModalAndGoBack();
            }
        },
        [reset, t, assemblyid, uiStore, printStatusMessage, closeSaveFormModalAndGoBack, isDirty, assemblyStore]
    );

    const closeAllLists = useCallback(() => {
        setZipItems([]);
        setNameItems([]);
        setStreetItems([]);
        setStreetNumberItems([]);
    }, []);

    const onResetForm = useCallback(
        (e: any) => {
            e.preventDefault();
            if (isDirty) {
                const zip = assemblyStore.currentAssembly?.zip || "";
                const city = assemblyStore.currentAssembly?.city || "";
                const street = assemblyStore.currentAssembly?.street || "";
                const housenumber = assemblyStore.currentAssembly?.housenumber || "";

                if (assemblyStore.currentAssembly) {
                    reset({
                        date: assemblyStore.currentAssembly?.date || "",
                        state: assemblyStore.currentAssembly?.state || ASSEMBLY_STATE.PREPARING,
                        zip: zip,
                        city: city,
                        street: street,
                        housenumber: housenumber,
                        title: assemblyStore.currentAssembly.title || "",
                        locationdetail: assemblyStore.currentAssembly.locationdetail || "",
                        motiondeadline: assemblyStore.currentAssembly.motiondeadline || null
                    });

                    closeAllLists();

                    setZipWasSelected(true);
                    setCityWasSelected(true);
                    setStreetWasSelected(true);
                    setStreetNumberWasSelected(true);

                    setCurrentZip(zip);
                    setCurrentCityName(city);
                    setCurrentStreet(street);
                    setCurrentStreetNumber(housenumber);

                    setValue("zip", zip);
                    setValue("city", city);
                    setValue("street", street);
                    setValue("housenumber", housenumber);
                } else {
                    reset({
                        date: "",
                        state: ASSEMBLY_STATE.PREPARING,
                        zip: "",
                        city: "",
                        street: "",
                        housenumber: "",
                        title: "",
                        locationdetail: "",
                        motiondeadline: null
                    });
                }
            }

            assemblyStore.setCurrentAssemblyRealestates(assemblyStore.currentAssembly?.assemblyrealestates ?? []);

            assemblyStore.setUnsavedAgendaItems([]);

            if (assemblyStore.agendaItemsHaveChanged) {
                assemblyStore.setResetAgendaItems(true);
            }

            if (assemblyStore.currentAssemblyRealestatesHaveChanged) {
                assemblyStore.setCurrentAssemblyRealestatesHaveChanged(false);
            }
        },
        [reset, closeAllLists, setValue, isDirty, assemblyStore]
    );

    const onDeleteAssembly = useCallback(async () => {
        await assemblyStore.deleteAssemblyById(assemblyStore.currentAssembly?.id);
        if (assemblyStore.isSuccessfullyDeletedAssembly) {
            history.replace(selectRoute(Route.assemblies, authStore.user?.role));
        }
    }, [assemblyStore, history, authStore.user?.role]);

    useEffect(() => {
        if (getValues("date")) {
            assemblyStore.setCurrentAssemblyTitle(
                `${getValues("title")} am ${getDateWithLongMonth(getValues("date"))}`
            );
        } else {
            assemblyStore.setCurrentAssemblyTitle(getValues("title"));
        }
    }, [t, getValues, assemblyStore]);

    useEffect(() => {
        assemblyStore.setCurrentAssemblyTitle(`${getValues("title")} am ${getDateWithLongMonth(watchDate)}`);
    }, [watchDate, getValues, assemblyStore]);

    useEffect(() => {
        if (getValues("date")) {
            assemblyStore.setCurrentAssemblyTitle(`${watchTitle} am ${getDateWithLongMonth(getValues("date"))}`);
        } else {
            assemblyStore.setCurrentAssemblyTitle(watchTitle);
        }
    }, [watchTitle, getValues, assemblyStore]);

    useEffect(() => {
        assemblyStore.setIsAssemblyPDFExists(true);
        assemblyStore.setCurrentAssemblyRealestatesHaveChanged(false);
        // eslint-disable-next-line
    }, []);

    let filesCount = 0;
    if (assemblyStore.oldAssemblyWithGeneralDocuments === false) {
        filesCount = assemblyStore.currentAssembly?.summaryassemblyfilesCount ?? 0;
        if (filesCount <= 0) {
            assemblyStore.setIsAssemblyFilesExists(false);
        }
    }

    const isNotificationButtonDisplayed =
        assemblyStore.currentAssembly != null &&
        assemblyStore.currentAssembly?.state !== undefined &&
        assemblyStore.currentAssembly?.state === ASSEMBLY_STATE.AGENDA_PUBLISHED;
    const isInlineCommunicationDisplayed =
        assemblyStore.currentAssembly != null &&
        !assemblyStore.isNewAssembly &&
        assemblyStore.currentAssemblyRealestates.length > 0;

    const mapStateToTranslation = (): string => {
        switch (assemblyStore.currentAssembly?.state) {
            case ASSEMBLY_STATE.PREPARING:
                return t("screens.realestate.assembly.edit_state.preparing");
            case ASSEMBLY_STATE.AGENDA_PUBLISHED:
                return t("screens.realestate.assembly.edit_state.agenda_published");
            case ASSEMBLY_STATE.DATE_PUBLISHED:
                return t("screens.realestate.assembly.edit_state.date_published");
            case ASSEMBLY_STATE.PROTOCOL_PUBLISHED:
                return t("screens.realestate.assembly.edit_state.protocol_published");
            case ASSEMBLY_STATE.CLOSED:
                return t("screens.realestate.assembly.edit_state.closed");
            default:
                return "";
        }
    };

    return (
        <>
            {saveFormModal}
            <UI.Row className="row" style={{ marginTop: 50 }} />
            <UI.Row>
                <UI.Col>
                    {isReadOnly ? (
                        <>
                            <div className="BlockedBanner">
                                <div className="BannerLeft">
                                    <UI.Icon className="LockIcon" icon={UI.SVGIcon.Lock} size={"small"} />
                                    <div className="BannerText">{t("screens.assemblies.agenda.locked_info")}</div>
                                </div>
                                <div className="BannerLink" onClick={onClickUnlockAssembly}>
                                    {t("screens.assemblies.agenda.unlock").toUpperCase()}
                                </div>
                            </div>
                            <AssemblyUnlockModal />
                        </>
                    ) : null}
                    <UI.Form autoComplete="nope">
                        <div className="AssemblyFormBasisDataContainer mb-3">
                            <div className="AssemblyBasisDataTitle mb-3">
                                {t("screens.realestate.assembly.basic_data")}
                            </div>
                            <UI.Row className="mb-2">
                                <UI.Col>
                                    <UI.Input
                                        className={dirtyFields.title ? "changed" : ""}
                                        label={`${t("screens.realestate.assembly.title_text")} *`}
                                        {...register("title")}
                                        type="text"
                                        autoComplete="off"
                                        disabled={isReadOnly}
                                        errorMsg={
                                            errors.title
                                                ? errors.title.message
                                                : watchTitle.length > 100
                                                  ? `${t("screens.realestate.assembly.title_text_too_large")}`
                                                  : undefined
                                        }
                                    />
                                </UI.Col>
                            </UI.Row>
                            <UI.Row>
                                <UI.Col>
                                    <UI.Row className="mb-2">
                                        <UI.Col>
                                            <div className="mb-3">
                                                <label className="form-label" htmlFor="date">{`${t(
                                                    "screens.realestate.assembly.date"
                                                )} *`}</label>
                                                <div className="input-group">
                                                    <input
                                                        className={`${dirtyFields.date ? "changed" : ""} form-control`}
                                                        type="datetime-local"
                                                        id="date"
                                                        {...register("date")}
                                                        min={Date.now()}
                                                        disabled={isReadOnly}
                                                    />
                                                    {errors.date && errors.date.message ? (
                                                        <div className="d-block invalid-feedback">
                                                            {errors.date.message}
                                                        </div>
                                                    ) : undefined}
                                                </div>
                                            </div>
                                        </UI.Col>
                                    </UI.Row>
                                    <UI.Row className="mb-2">
                                        <UI.Col>
                                            <ZipAutocompleteInput
                                                setError={setError}
                                                errors={errors}
                                                setSearchType={setSearchType}
                                                zipItems={zipItems}
                                                setZipItems={setZipItems}
                                                setNameItems={setNameItems}
                                                searchType={searchType}
                                                setZipWasSelected={setZipWasSelected}
                                                setCityWasSelected={setCityWasSelected}
                                                closeAllLists={closeAllLists}
                                                setValue={setValue}
                                                register={register}
                                                watchZip={watchZip}
                                                zipWasSelected={zipWasSelected}
                                                setStreetWasSelected={setStreetWasSelected}
                                                setStreetNumberWasSelected={setStreetNumberWasSelected}
                                                currentZip={currentZip}
                                                setCurrentZip={setCurrentZip}
                                                setCurrentCityName={setCurrentCityName}
                                                dirtyFields={dirtyFields}
                                                disabled={isReadOnly}
                                            />
                                        </UI.Col>
                                        {error === ERROR_TYPE.ZIP ? (
                                            <p className="alert text-danger">
                                                {t("screens.realestate.assembly.error.zip")}
                                            </p>
                                        ) : undefined}
                                    </UI.Row>
                                    <UI.Row className="mb-2">
                                        <UI.Col>
                                            <StreetAutocompleteInput
                                                register={register}
                                                errors={errors}
                                                setValue={setValue}
                                                watch={watch}
                                                watchStreet={watchStreet}
                                                setError={setError}
                                                setSearchType={setSearchType}
                                                searchType={searchType}
                                                setZipWasSelected={setZipWasSelected}
                                                closeAllLists={closeAllLists}
                                                setStreetWasSelected={setStreetWasSelected}
                                                setStreetNumberWasSelected={setStreetNumberWasSelected}
                                                cityWasSelected={cityWasSelected}
                                                streetWasSelected={streetWasSelected}
                                                setNumbersOfSelectedStreet={setNumbersOfSelectedStreet}
                                                streetItems={streetItems}
                                                setStreetItems={setStreetItems}
                                                zipWasSelected={zipWasSelected}
                                                currentStreet={currentStreet}
                                                setCurrentStreet={setCurrentStreet}
                                                dirtyFields={dirtyFields}
                                                disabled={isReadOnly}
                                            />
                                        </UI.Col>
                                        {error === ERROR_TYPE.STREET ? (
                                            <p className="alert text-danger">
                                                {t("screens.realestate.assembly.error.street")}
                                            </p>
                                        ) : undefined}
                                    </UI.Row>
                                    <UI.Row>
                                        <UI.Col>
                                            <UI.Input
                                                label={`${t("screens.realestate.assembly.state")}`}
                                                id="state"
                                                errorMsg={errors.state ? errors.state.message : undefined}
                                                className={dirtyFields.state ? "changed" : ""}
                                                readOnly
                                                value={mapStateToTranslation()}
                                            />
                                        </UI.Col>
                                    </UI.Row>
                                </UI.Col>
                                <UI.Col>
                                    <UI.Row className="mb-2">
                                        <UI.Col>
                                            <div className="mb-3">
                                                <label className="form-label" htmlFor="date">{`${t(
                                                    "screens.realestate.assembly.motiondeadline"
                                                )}`}</label>
                                                <div className="input-group">
                                                    <input
                                                        className={`${
                                                            dirtyFields.motiondeadline ? "changed" : ""
                                                        } form-control`}
                                                        type="date"
                                                        id="motiondeadline"
                                                        {...register("motiondeadline")}
                                                        disabled={isReadOnly}
                                                    />
                                                </div>
                                            </div>
                                        </UI.Col>
                                    </UI.Row>
                                    <UI.Row className="mb-2">
                                        <UI.Col>
                                            <CityAutocompleteInput
                                                register={register}
                                                errors={errors}
                                                setValue={setValue}
                                                watchCity={watchCity}
                                                setError={setError}
                                                setSearchType={setSearchType}
                                                zipItems={zipItems}
                                                setZipItems={setZipItems}
                                                nameItems={nameItems}
                                                setNameItems={setNameItems}
                                                searchType={searchType}
                                                setZipWasSelected={setZipWasSelected}
                                                setCityWasSelected={setCityWasSelected}
                                                closeAllLists={closeAllLists}
                                                setStreetWasSelected={setStreetWasSelected}
                                                setStreetNumberWasSelected={setStreetNumberWasSelected}
                                                cityWasSelected={cityWasSelected}
                                                streetWasSelected={streetWasSelected}
                                                setCurrentZip={setCurrentZip}
                                                currentCityName={currentCityName}
                                                setCurrentCityName={setCurrentCityName}
                                                dirtyFields={dirtyFields}
                                                disabled={isReadOnly}
                                            />
                                        </UI.Col>
                                        {error === ERROR_TYPE.CITY ? (
                                            <p className="alert text-danger">
                                                {t("screens.realestate.assembly.error.city")}
                                            </p>
                                        ) : undefined}
                                    </UI.Row>
                                    <UI.Row className="mb-2">
                                        <UI.Col>
                                            <StreetNumberAutocompleteInput
                                                register={register}
                                                errors={errors}
                                                setValue={setValue}
                                                watchHousenumber={watchHousenumber}
                                                setError={setError}
                                                setSearchType={setSearchType}
                                                searchType={searchType}
                                                setZipWasSelected={setZipWasSelected}
                                                closeAllLists={closeAllLists}
                                                setStreetWasSelected={setStreetWasSelected}
                                                setStreetNumberWasSelected={setStreetNumberWasSelected}
                                                streetWasSelected={streetWasSelected}
                                                setStreetItems={setStreetItems}
                                                numbersOfSelectedStreet={numbersOfSelectedStreet}
                                                setNumbersOfSelectedStreet={setNumbersOfSelectedStreet}
                                                setStreetNumberItems={setStreetNumberItems}
                                                streetNumberWasSelected={streetNumberWasSelected}
                                                streetNumberItems={streetNumberItems}
                                                dirtyFields={dirtyFields}
                                                getValues={getValues}
                                                currentStreetNumber={currentStreetNumber}
                                                setCurrentStreetNumber={setCurrentStreetNumber}
                                                disabled={isReadOnly}
                                            />
                                            <input type="hidden" />
                                        </UI.Col>
                                        {error === ERROR_TYPE.STREETNUMBER ? (
                                            <p className="alert text-danger">
                                                {t("screens.realestate.assembly.error.streetnumber")}
                                            </p>
                                        ) : undefined}
                                    </UI.Row>
                                    <UI.Row className="mb-2">
                                        <UI.Col>
                                            <UI.Input
                                                className={dirtyFields.locationdetail ? "changed" : ""}
                                                label={t("screens.realestate.assembly.locationdetail").toString()}
                                                {...register("locationdetail")}
                                                type="text"
                                                autoComplete="off"
                                                disabled={isReadOnly}
                                            />
                                        </UI.Col>
                                    </UI.Row>
                                </UI.Col>
                            </UI.Row>
                        </div>
                        <div></div>
                        <div className="AssemblyFormBasisDataContainer mb-3">
                            <UI.Row>
                                <UI.Col>
                                    <AssemblyRealestateList />
                                </UI.Col>
                            </UI.Row>
                        </div>
                        {isInlineCommunicationDisplayed ? (
                            <InlineCommunicationTimelineEntry
                                assemblyId={assemblyid || assemblyStore.currentAssembly?.id || ""}
                                entryState={COMMUNICATION_TIMELINE_ENTRY.DATE_PUBLISHED}
                            />
                        ) : undefined}
                        <div className="mb-4">
                            <UI.Button
                                disabled={
                                    (!assemblyStore.assembliesEditFormHasDirtyFields &&
                                        !assemblyStore.currentAssemblyRealestatesHaveChanged) ||
                                    assemblyStore.assembliesEditFormStatus === FORM_STATUS.SAVING_FORM ||
                                    watchTitle.length > 100
                                }
                                onClick={handleSubmit(handleFormSubmit)}
                                label={t("labels.save")}
                                id="assembly-submit-button"
                                className="AssemblyFormButton"
                                variant="primary"
                            />
                            <UI.Button
                                label={t("labels.discard")}
                                disabled={
                                    (!assemblyStore.assembliesEditFormHasDirtyFields &&
                                        !assemblyStore.currentAssemblyRealestatesHaveChanged) ||
                                    assemblyStore.assembliesEditFormStatus === FORM_STATUS.SAVING_FORM ||
                                    watchTitle.length > 100
                                }
                                onClick={onResetForm}
                                className={"AssemblyFormButton ml-sm-2 mt-2 mt-sm-0"}
                                variant="outline-primary"
                            />
                            <UI.ConfirmationDialog
                                key="AssemblyDeleteDialog"
                                buttonText={t("labels.delete")}
                                modalTitle={t("labels.delete")}
                                confirmationQuestion={t("screens.realestate.assembly.edit.delete_confirmation")}
                                inProgressText={t("screens.realestate.assembly.edit.delete_in_progress")}
                                onConfirmation={onDeleteAssembly}
                                buttonClasses={`AssemblyFormButton mt-2 mt-sm-0 ${
                                    isNotificationButtonDisplayed ? "ml-sm-0 mt-sm-2 mt-lg-0 ml-lg-2" : "ml-sm-2"
                                }`}
                                isButtonDisabled={assemblyStore.currentAssemblyRealestates.length < 1}
                                isDangerousOperation
                            />
                        </div>
                    </UI.Form>
                </UI.Col>
            </UI.Row>
        </>
    );
};

export const AssemblyDetailEditForm = Sentry.withProfiler(observer(AssemblyDetailEditFormBase));
